/**
 * The storefront root's one request, started before the app has booted.
 *
 * A storefront root renders nothing until the store comes back (it inlines the
 * home page, sales-page embed and all). That request used to start only after the
 * entry bundle, then StoreHome's chunk and every chunk it imports, had arrived —
 * 3.7s on a phone on 4G, against 0.4s for the HTML. index.html now carries a tiny
 * script (src/boot/earlyStore.ts) that starts it while the entry bundle is still
 * downloading, and api.ts hands that response to the first GET for the same path.
 *
 * Pure, no DOM: the boot script, resources.ts and the unit tests share it, so the
 * path the script fetches can never drift from the one StoreHome asks for.
 */
import { hostMode, type HostMode } from "./host";
import { isStorefrontRootPath } from "./storefrontRoutes";

/** Where the boot script leaves its fetch on `window`, taken once by api.ts. */
export const EARLY_STORE_KEY = "__shzEarlyStore";

export interface EarlyStore {
  /** API path without the `/api` prefix, exactly as resources.ts requests it. */
  path: string;
  res: Promise<Response>;
}

/** The storefront landing request for a subdomain or custom-domain store. */
export function storeLandingPath(mode: Exclude<HostMode, { kind: "app" }>, bioOnly = false): string {
  const base = mode.kind === "domain" ? `/public/d/${encodeURIComponent(mode.host)}/store` : `/public/store/${mode.subdomain}`;
  return bioOnly ? `${base}?view=bio` : base;
}

/**
 * The path to start early for this URL, or null when the page about to boot is not
 * a storefront root. `?__store=` / `?__domain=` force a storefront on a local host,
 * as currentHostMode does.
 */
export function earlyStorePath(
  loc: { hostname: string; pathname: string; search: string },
  apex: string,
  alsoServed: string[] = [],
): string | null {
  if (!isStorefrontRootPath(loc.pathname)) return null;
  const params = new URLSearchParams(loc.search);
  const sub = params.get("__store");
  const dom = params.get("__domain");
  const mode: HostMode = sub
    ? { kind: "subdomain", subdomain: sub.trim().toLowerCase() }
    : dom
      ? { kind: "domain", host: dom.trim().toLowerCase() }
      : hostMode(loc.hostname, apex, alsoServed);
  return mode.kind === "app" ? null : storeLandingPath(mode);
}
